/** One expandable response panel. Mail and long responses start full-height.
 * Only the handle accepts swipe gestures; scrolling/selecting message text
 * never dismisses it. Existing backdrop, Escape and scroll-lock are retained.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { MobileBottomSheet } from "../mobile/MobileBottomSheet";
import NarrationBlock from "./renderers/NarrationBlock";
import BookingPreview from "./renderers/BookingPreview";
import ExpensePreview from "./renderers/ExpensePreview";
import ExpenseReceipt from "./renderers/ExpenseReceipt";
import EarningsAnswer from "./renderers/EarningsAnswer";
import DiaryAnswer from "./renderers/DiaryAnswer";
import HelpAnswer from "./renderers/HelpAnswer";
import NavigateChoice from "./renderers/NavigateChoice";
import MailAnswer from "./renderers/MailAnswer";
import UCReportAnswer from "./renderers/UCReportAnswer";
import "./UniversalIntentResponseSheet.css";

function pickRenderer(kind) {
  switch (kind) {
    case "booking-preview": return BookingPreview;
    case "expense-preview": return ExpensePreview;
    case "expense-receipt": return ExpenseReceipt;
    case "earnings-answer": return EarningsAnswer;
    case "diary-answer": return DiaryAnswer;
    case "help-answer": return HelpAnswer;
    case "navigate-choice": return NavigateChoice;
    case "mail-answer": return MailAnswer;
    case "uc-report": return UCReportAnswer;
    default: return NarrationBlock;
  }
}

export function UniversalIntentResponseSheet({ open, presentation, listening = false, onDismiss, onConfirmNavigate }) {
  const p = presentation || { kind: "narration", title: null, narration: "One moment…", data: null };
  const generous = ["mail-answer", "uc-report"].includes(p.kind) || (p.narration || "").length > 700;
  const [expanded, setExpanded] = useState(generous);
  const panelRef = useRef(null);
  const dragStart = useRef(null);
  const expand = useCallback(() => setExpanded(true), []);
  useEffect(() => { if (open) setExpanded(generous); }, [open, generous]);
  useEffect(() => { if (open) panelRef.current?.focus?.({ preventScroll: true }); }, [open]);
  const Renderer = pickRenderer(p.kind);

  const handleEnd = (event) => {
    if (dragStart.current === null) return;
    const end = event.changedTouches?.[0]?.clientY;
    const distance = typeof end === "number" ? end - dragStart.current : 0;
    dragStart.current = null;
    if (distance < -50) setExpanded(true);
    if (distance > 60) {
      if (expanded) setExpanded(false);
      else onDismiss?.();
    }
  };
  const keepFocus = (event) => {
    if (event.key !== "Tab") return;
    const focusable = [...(panelRef.current?.querySelectorAll(
      'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex="0"]',
    ) || [])].filter((node) => node.getClientRects().length);
    if (!focusable.length) { event.preventDefault(); return; }
    const first = focusable[0], last = focusable[focusable.length - 1];
    if (event.shiftKey && (document.activeElement === first || document.activeElement === panelRef.current)) {
      event.preventDefault(); last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault(); first.focus();
    }
  };

  return <MobileBottomSheet open={open} onClose={onDismiss} testid="universal-intent-response-sheet" bareContent>
    <div ref={panelRef} tabIndex={-1} role="dialog" aria-modal="true"
      aria-labelledby="karen-response-title" data-testid="universal-intent-response-sheet"
      data-expanded={expanded ? "true" : "false"}
      className={`karen-response-panel${expanded ? " is-expanded" : ""}`}
      onClick={(event) => event.stopPropagation()} onKeyDown={keepFocus}>
      <div className="karen-response-handle" data-testid="ui-response-sheet-swipe" aria-hidden="true"
        onTouchStart={(event) => { dragStart.current = event.touches?.[0]?.clientY ?? null; }}
        onTouchEnd={handleEnd} onTouchCancel={() => { dragStart.current = null; }}>
        <span />
      </div>
      <header className="karen-response-header">
        <h2 id="karen-response-title">{p.title || "Karen’s response"}</h2>
        <div className="karen-response-controls">
          <button type="button" className="karen-panel-button" aria-expanded={expanded}
            data-testid="karen-response-expand" onClick={() => setExpanded((value) => !value)}>
            {expanded ? "Make smaller" : "Expand"}
          </button>
          <button type="button" className="karen-panel-button" onClick={onDismiss}
            data-testid="universal-intent-response-sheet-close" aria-label="Close response">Close</button>
        </div>
      </header>
      <div className="karen-response-scroll" data-testid="universal-intent-response-body">
        <div className="karen-response-content">
          <Renderer presentation={p} onConfirmNavigate={onConfirmNavigate} onDismiss={onDismiss} onExpand={expand} />
        </div>
      </div>
      {listening && <footer className="karen-response-listening" data-testid="karen-listening-indicator" role="status">
        Listening…
      </footer>}
    </div>
  </MobileBottomSheet>;
}

export default UniversalIntentResponseSheet;
